import { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, doc, setDoc, updateDoc, getDocs } from 'firebase/firestore';

const EmployeeManagement = ({ user, setEmployees }) => {
  const [employees, setLocalEmployees] = useState([]);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    employeeId: '',
    email: '',
    phone: '',
    role: '',
    payRate: '',
    hireDate: '',
    active: true
  });
  const [editEmployeeId, setEditEmployeeId] = useState(null);

  useEffect(() => {
    if (user) {
      fetchEmployees();
    }
  }, [user]);

  // Fetch Employees
  const fetchEmployees = async () => {
    const snapshot = await getDocs(collection(db, 'employees'));
    const employeeList = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name || '',
        employeeId: data.employeeId || '',
        email: data.email || '',
        phone: data.phone || '',
        role: data.role || '',
        payRate: parseFloat(data.payRate) || 0, // Default to 0 if undefined
        hireDate: data.hireDate || null,
        active: data.active !== undefined ? data.active : true,
        createdAt: data.createdAt || null
      };
    });
    setLocalEmployees(employeeList);
    setEmployees(employeeList); // Update parent state for TimecardSystem
  };

  // Add/Edit Employee
  const saveEmployee = async () => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    const employeeData = {
      name: newEmployee.name,
      employeeId: newEmployee.employeeId,
      email: newEmployee.email,
      phone: newEmployee.phone,
      role: newEmployee.role,
      payRate: parseFloat(newEmployee.payRate) || 0,
      hireDate: newEmployee.hireDate ? new Date(newEmployee.hireDate) : new Date(),
      active: newEmployee.active,
      createdAt: new Date()
    };
    if (editEmployeeId) {
      await updateDoc(doc(db, 'employees', editEmployeeId), employeeData);
      setEditEmployeeId(null);
    } else {
      await setDoc(doc(db, 'employees', newEmployee.employeeId), employeeData);
    }
    setNewEmployee({
      name: '',
      employeeId: '',
      email: '',
      phone: '',
      role: '',
      payRate: '',
      hireDate: '',
      active: true
    });
    fetchEmployees();
  };

  // Edit Employee
  const editEmployee = (employee) => {
    setEditEmployeeId(employee.id);
    setNewEmployee({
      name: employee.name,
      employeeId: employee.employeeId,
      email: employee.email,
      phone: employee.phone,
      role: employee.role,
      payRate: employee.payRate.toString(),
      hireDate: employee.hireDate ? employee.hireDate.toDate().toISOString().slice(0, 10) : '',
      active: employee.active
    });
  };

  // Toggle Employee Status
  const toggleEmployeeStatus = async (employeeId, active) => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    await updateDoc(doc(db, 'employees', employeeId), { active: !active });
    fetchEmployees();
  };

  return (
    <div>
      <h2>Employee Management</h2>
      <div className="inventory-section">
        <h3>{editEmployeeId ? 'Edit Employee' : 'Add New Employee'}</h3>
        <div className="inventory-form">
          <input
            value={newEmployee.name}
            onChange={e => setNewEmployee({ ...newEmployee, name: e.target.value })}
            placeholder="Full Name"
          />
          <input
            value={newEmployee.employeeId}
            onChange={e => setNewEmployee({ ...newEmployee, employeeId: e.target.value })}
            placeholder="Employee ID"
            disabled={editEmployeeId !== null} // Prevent changing ID when editing
          />
          <input
            value={newEmployee.email}
            onChange={e => setNewEmployee({ ...newEmployee, email: e.target.value })}
            placeholder="Email"
            type="email"
          />
          <input
            value={newEmployee.phone}
            onChange={e => setNewEmployee({ ...newEmployee, phone: e.target.value })}
            placeholder="Phone Number"
            type="tel"
          />
          <input
            value={newEmployee.role}
            onChange={e => setNewEmployee({ ...newEmployee, role: e.target.value })}
            placeholder="Role (e.g., Barista)"
          />
          <input
            value={newEmployee.payRate}
            onChange={e => setNewEmployee({ ...newEmployee, payRate: e.target.value })}
            placeholder="Pay Rate ($/hr)"
            type="number"
            step="0.01"
          />
          <input
            value={newEmployee.hireDate}
            onChange={e => setNewEmployee({ ...newEmployee, hireDate: e.target.value })}
            placeholder="Hire Date"
            type="date"
          />
          <div className="checkbox-container">
            <label>Active:</label>
            <input
              type="checkbox"
              checked={newEmployee.active}
              onChange={e => setNewEmployee({ ...newEmployee, active: e.target.checked })}
            />
          </div>
        </div>
        <div className="inventory-form-buttons">
          <button className="save-button" onClick={saveEmployee}>
            {editEmployeeId ? 'Update' : 'Add'} Employee
          </button>
          {editEmployeeId && (
            <button
              className="cancel-button"
              onClick={() => {
                setEditEmployeeId(null);
                setNewEmployee({
                  name: '',
                  employeeId: '',
                  email: '',
                  phone: '',
                  role: '',
                  payRate: '',
                  hireDate: '',
                  active: true
                });
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      <h3>Employees</h3>
      <div className="inventory-grid">
        {employees.map(employee => (
          <div key={employee.id} className="inventory-card">
            <h4>{employee.name || 'N/A'}</h4>
            <p>ID: {employee.employeeId || 'N/A'}</p>
            <p>Email: {employee.email || 'N/A'}</p>
            <p>Phone: {employee.phone || 'N/A'}</p>
            <p>Role: {employee.role || 'N/A'}</p>
            <p>Pay Rate: ${employee.payRate ? employee.payRate.toFixed(2) : '0.00'}/hr</p>
            <p>Hire Date: {employee.hireDate ? employee.hireDate.toDate().toLocaleDateString() : 'N/A'}</p>
            <p>Status: {employee.active ? 'Active' : 'Inactive'}</p>
            <div className="actions">
              <button className="edit-button" onClick={() => editEmployee(employee)}>
                Edit
              </button>
              <button className="toggle-button" onClick={() => toggleEmployeeStatus(employee.id, employee.active)}>
                Toggle Status
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeManagement;